import React, { useState } from 'react';
import { Globe2 } from 'lucide-react';
import { flagUrl, flagSrcSet } from '../data/numberPricing';

/**
 * Country flag image with graceful fallback.
 * Uses flagcdn.com SVG/PNG flags that render correctly on every OS,
 * including Windows where emoji flags don't display.
 *
 * Props:
 *   code   - ISO-2 country code (case insensitive)
 *   size   - Tailwind size token: "sm" | "md" | "lg" | "xl"
 *   round  - true (default) for circular badge, false for inline rectangle
 */
export default function Flag({ code, size = 'md', round = true, className = '' }) {
  const [errored, setErrored] = useState(false);

  const sizeMap = {
    sm: { wrap: 'w-7 h-7', img: 'w-5 h-5' },
    md: { wrap: 'w-10 h-10', img: 'w-7 h-7' },
    lg: { wrap: 'w-12 h-12', img: 'w-8 h-8' },
    xl: { wrap: 'w-16 h-16', img: 'w-12 h-12' }
  };
  const s = sizeMap[size] || sizeMap.md;
  const url = flagUrl(code);
  const srcSet = flagSrcSet(code);

  if (round) {
    return (
      <span
        className={`inline-flex items-center justify-center ${s.wrap} rounded-full bg-white border border-slate-200 shadow-sm overflow-hidden flex-shrink-0 ${className}`}
        aria-label={code}
      >
        {url && !errored ? (
          <img
            src={url}
            srcSet={srcSet || undefined}
            alt={code}
            className={`${s.img} object-cover rounded-full`}
            loading="lazy"
            decoding="async"
            onError={() => setErrored(true)}
          />
        ) : (
          <Globe2 size={16} className="text-slate-400" />
        )}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center overflow-hidden rounded-sm flex-shrink-0 ${className}`}
      aria-label={code}
    >
      {url && !errored ? (
        <img
          src={url}
          srcSet={srcSet || undefined}
          alt={code}
          className="w-7 h-5 object-cover rounded-sm shadow-sm"
          loading="lazy"
          decoding="async"
          onError={() => setErrored(true)}
        />
      ) : (
        <Globe2 size={14} className="text-slate-400" />
      )}
    </span>
  );
}
